import { useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import type { VoidFunctionComponent } from 'react';

import { useInputValue } from '../../hooks';
import Button, { ButtonGroup, TooltipButton } from '../generic/Button';
import Flex from '../generic/Flex';
import FormGroup from '../generic/Forms/FormGroup';
import LabeledSelect from '../generic/Forms/LabeledSelect';
import LabeledTextInput from '../generic/Forms/LabeledTextInput';
import Text from '../generic/Text';
import type { DependencyFilterVersionType } from '../../../shared/model-typings';
import type { SelectOption } from '../../types';
import type { RuleModalType } from '../policies/quality/types';

type NewQualityRule = {
  name: string,
  startVersion: string,
  endVersion?: string,
  versionType: DependencyFilterVersionType,
};

type Props = {
  ruleType: RuleModalType;
  show: boolean;
  onHide: () => void;
  onAddRule: (ruleOptions: NewQualityRule) => void;
};

type SubmitTooltip = 'A dependency name is required.' | 'Please enter a start version.' | 'Please enter an end version.';

const SELECT_OPTIONS: Array<SelectOption<DependencyFilterVersionType>> = [
  {
    value: 'RANGE',
    label: 'is between',
  },
  {
    value: 'EQUAL',
    label: 'is exactly',
  },
  {
    value: 'GREATER',
    label: 'is greater than',
  },
  {
    value: 'LESS',
    label: 'is less than',
  },
  {
    value: 'ALL',
    label: 'all versions',
  },
];

/**
 * ModalAddDependencyFilterRule is a modal which is used to create new
 * quality policy allow/block rules. It contains a text input for users
 * to input a dependency name, as well as a semver range selector.
 *
 * @param {boolean} show - If the modal is displayed.
 * @param {(ruleOptions: NewQualityRule) => void} onAddRule - If the modal is displayed.
 * @param {() => void} onHide - Function to hide the modal.
 * @param {RuleModalType} ruleType - Type of rule to be created.
 */
const ModalAddDependencyFilterRule: VoidFunctionComponent<Props> = ({
  show,
  onAddRule,
  onHide,
  ruleType,
}) => {
  const [depName, setDepName] = useInputValue('');
  const [startVersion, setStartVersion] = useInputValue('');
  const [endVersion, setEndVersion] = useInputValue('');
  const [filterType, setFilterType] = useState<DependencyFilterVersionType>('RANGE');

  const isStartVersionDisplayed = ['RANGE', 'GREATER', 'EQUAL'].includes(filterType);
  const isEndVersionDisplayed = ['LESS', 'RANGE'].includes(filterType);

  const selectedFilterOption = useMemo(
    () => SELECT_OPTIONS.find((opt) => opt.value === filterType),
    [filterType],
  );

  //  Stored in a useMemo because so we can use if/else branching, not so much for performance.
  const submitTooltip = useMemo((): SubmitTooltip | null => {
    if (!depName) {
      return 'A dependency name is required.';
    }
    if (isStartVersionDisplayed && !startVersion) {
      return 'Please enter a start version.';
    }
    if (isEndVersionDisplayed && !endVersion) {
      return 'Please enter an end version.';
    }
    return null;
  }, [depName, startVersion, isStartVersionDisplayed, endVersion, isEndVersionDisplayed]);

  const handleHideModal = () => {
    //  Hide modal
    onHide();
    //  Reset state.
    setDepName('');
    setStartVersion('');
    setEndVersion('');
    setFilterType('RANGE');
  };

  const handleSubmit = () => {
    if (!submitTooltip) {
      onAddRule({
        name: depName,
        startVersion,
        endVersion,
        versionType: filterType,
      });
      handleHideModal();
    }
  };

  return (
    <Modal show={show} onHide={handleHideModal} animation>
      <Modal.Header closeButton>
        <Text.H2>
          {ruleType === 'whitelisted_dependency' ? 'Create allow rule' : 'Create block rule'}
        </Text.H2>
        <Text.Paragraph>
          {ruleType === 'whitelisted_dependency'
            ? 'Add an exact-match name and semver range for packages which will never raise an issue, even when in violation of another policy.'
            : 'Add a regex-match name and semver range for packages which will always raise an issue, regardless of if they are in violation of another policy.'}
        </Text.Paragraph>
      </Modal.Header>
      <Modal.Body>
        <FormGroup>
          <div>
            <LabeledTextInput
              label="Name"
              placeholder="Package name"
              value={depName}
              onChange={setDepName}
            />
            <Text.Paragraph>
              {ruleType === 'blacklisted_dependency' ? '*Will do a regex match on provided name' : '*Will only match on exact name'}
            </Text.Paragraph>
          </div>
          <Flex direction="row" align="flex-start" gap="8px">
            <LabeledSelect
              id="quality-version-range-select"
              label="Version range"
              options={SELECT_OPTIONS}
              value={selectedFilterOption}
              onChange={(e) => {
                if (e) {
                  setFilterType(e.value);
                }
              }}
            />
            {isStartVersionDisplayed && (
              <LabeledTextInput
                label="Start version"
                placeholder="1.0.0"
                size="small"
                value={startVersion}
                onChange={setStartVersion}
              />
            )}
            {isStartVersionDisplayed && isEndVersionDisplayed && (
              <span className="fossa-font-size-xs padding-top-lg">
                and
              </span>
            )}
            {isEndVersionDisplayed && (
              <LabeledTextInput
                label="End version"
                placeholder="2.0.0"
                size="small"
                value={endVersion}
                onChange={setEndVersion}
              />
            )}
          </Flex>
        </FormGroup>
      </Modal.Body>
      <Modal.Footer>
        <ButtonGroup>
          <Button
            type="secondary"
            size="medium"
            onClick={onHide}
          >
            Cancel
          </Button>
          <TooltipButton
            id="quality-add-filter-rule"
            type="primary"
            size="medium"
            onClick={handleSubmit}
            tooltip={submitTooltip}
            showTooltip={!!submitTooltip}
            isDisabled={!!submitTooltip}
            isSubmitButton
          >
            Add rule
          </TooltipButton>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalAddDependencyFilterRule;
